import { isFulfilled } from "@reduxjs/toolkit";
import { FC, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { toast } from "react-toastify";
import DefaultImg from "../../../assets/img/default-image.svg";
import { DEFAULT_ITEMS_PER_PAGE } from "../../../constants/app";
import { STORAGE_KEYS } from "../../../constants/storageKeys";
import { useAppDispatch as useDispatch } from "../../../store";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import {
  CandidatesSortField,
  ICandidate,
  IPaginatedData,
  SortOrder,
} from "../../../store/centers/types";
import { PaginationLink } from "../../../store/superadmin/types";
import {
  rateCandidateThunk,
  toggleCandidatePreferredStatus,
} from "../../../store/vacancy/thunks";
import { TToggleCandidatePreferredParams } from "../../../store/vacancy/types";
import { getFormatedDate } from "../../../utils/dataParser";
import storage from "../../../utils/storage";
import { capitalizeName } from "../../../utils/stringUtils";
import PaginationControls from "../../Pagination/PaginationControls";
import CustomButton from "../../UI/Buttons/CustomButton";
import Block from "../../UI/Icons/Block";
import Cross from "../../UI/Icons/Cross";
import Eye from "../../UI/Icons/Eye";
import Repeat from "../../UI/Icons/Repeat";
import Star from "../../UI/Icons/Star";
import VerticalDots from "../../UI/Icons/VerticalDots";
import StarRating from "../../UI/StarRating";
import ListHeader from "./ListHeader";

interface CandidateItemProps {
  type: "preferred" | "approved" | "blocked";
  /** Assuming it's a string like "first_name" */
  selectedSortBy: string;
  /** If sort order is always "asc" or "desc" */
  selectedSortOrder: SortOrder;
  /** Function type with a string parameter */
  handleSortChange: (sortBy: CandidatesSortField, list: string) => void;
  candidateDatas: IPaginatedData<ICandidate> | null;
  handleBlock: (id: number) => void;
  handleProfile: (id: string) => Promise<void>;
  handleViewBlockedReason?: (uuid: string) => void;
  currentPage: number;
  totalPages: number;
  paginationLinks: PaginationLink[];
  onPageChange: (url: string | null) => void;
  setPerPageList: (value: number) => void;
  setCurrentPage: (value: number) => void;
  getAllCandidates: () => void;
}

const CandidateItem: FC<CandidateItemProps> = ({
  type,
  selectedSortBy,
  selectedSortOrder,
  handleSortChange,
  candidateDatas,
  handleBlock,
  handleProfile,
  handleViewBlockedReason,
  currentPage,
  totalPages,
  paginationLinks,
  onPageChange,
  setPerPageList,
  setCurrentPage,
  getAllCandidates,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const [openBlockedButton, setOpenBlockedButton] = useState<boolean>(false);
  const [viewSelectedCandidateProfile, setViewSelectedCandidateProfile] =
    useState<number | null>(0);
  const openBlockedRef = useRef<HTMLDivElement | null>(null);
  const openSideDropdownRef = useRef<HTMLDivElement | null>(null);
  const [openSideDropdownList, setOpenSideDropdownList] =
    useState<boolean>(false);
  const [candidateId, setCandidateId] = useState<number | null>(null);

  const handleCandidateStarRating = async (
    candidate_id: number,
    rating: number
  ) => {
    dispatch(showTransparentPreloaderAction());

    const payload = [
      {
        candidate_id: candidate_id,
        rating: rating,
        comments: "",
        centre_id: Number(storage.get(STORAGE_KEYS.SELECTED_CENTRE_ID)),
        vacancy_id: null,
        block: false,
        blocked_reason: "",
      },
    ];

    const rateRes = await dispatch(rateCandidateThunk(payload));

    if (isFulfilled(rateRes)) {
      dispatch(hideTransparentPreloaderAction());
      dispatch(showNotificationAction("Rating Saved"));
    }
  };

  const handleProfileView = (id: string) => {
    handleProfile(id);
    setOpenBlockedButton(false);
  };

  const handlePreferredCandidate = async (
    candidateData: ICandidate,
    action: "remove" | "mark" | null,
    type: boolean
  ) => {
    setOpenSideDropdownList(false);
    const payload: TToggleCandidatePreferredParams = {
      centre_id: Number(storage.get(STORAGE_KEYS.SELECTED_CENTRE_ID)),
      centre_uuid: String(storage.get(STORAGE_KEYS.SELECTED_CENTRE_UUID)),
      user_id: candidateData.id,
      user_uuid: candidateData.uuid,
      action: action,
      is_preferred: type,
    };
    const result = await dispatch(toggleCandidatePreferredStatus(payload));
    if (isFulfilled(result)) {
      if (action === "mark") {
        dispatch(showNotificationAction("Marked as Preferred"));
      } else if (action === "remove") {
        dispatch(showNotificationAction("Unmarked as Preferred"));
      } else {
        dispatch(showNotificationAction("Saved"));
      }
    } else {
      toast.error(
        "Mark Candidate as Preferred failed. Please select the centre and try again."
      );
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (openBlockedRef.current &&
          !openBlockedRef.current.contains(event.target as Node)) ||
        (openSideDropdownRef.current &&
          !openSideDropdownRef.current.contains(event.target as Node))
      ) {
        setOpenBlockedButton(false);
        setOpenSideDropdownList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <ListHeader
        type={type}
        selectedSortBy={selectedSortBy}
        selectedSortOrder={selectedSortOrder}
        handleSortChange={handleSortChange}
      />
      {candidateDatas && candidateDatas?.total > 0 ? (
        candidateDatas?.data.map((candidateData: ICandidate, index: number) => (
          <div
            key={index}
            className="border-bottom border-off-white p-15px d-flex flex-column flex-lg-row gap-20 align-items-lg-center"
          >
            <div className="flex-grow-1 text-break">
              <div className="d-flex gap-20 align-items-center">
                <div className="w-50px h-50px radius-50px">
                  <img
                    alt="Candidate"
                    width="50px"
                    height="50px"
                    className="img-responsive rounded-circle object-fit-cover"
                    src={candidateData.photo || DefaultImg}
                  />
                </div>
                <div className="d-flex flex-column gap-3 text-black">
                  <div className="fs-16 fw-700">
                    {capitalizeName(candidateData.first_name || "")} {capitalizeName(candidateData.last_name || "")}
                  </div>
                  <div className="fs-13 fw-400">
                    {candidateData.qualifications?.position_names ?? "-"}
                  </div>
                </div>
              </div>
            </div>
            <div className="min-w-212px d-flex gap-20 align-items-center">
              <div className="d-flex flex-column gap-3">
                <StarRating
                  rating={
                    candidateData?.rating?.latest_rating ||
                    candidateData?.rating?.average_rating ||
                    0
                  }
                  color={
                    !candidateData?.rating?.latest_rating
                      ? "#aaaaaa"
                      : "#51C2B9"
                  }
                />
                {!candidateData?.rating?.latest_rating &&
                  candidateData?.rating?.average_rating && (
                    <div className="fs-10 fw-400 text-grey-dark text-center">
                      Average Rating
                    </div>
                  )}
              </div>
              {type === "preferred" ? (
                <div className="bg-teal-pale border border-teal-dark py-4px px-8px radius-8px d-flex gap-3 align-items-center">
                  <div className="fs-13 fw-400 text-teal-dark">Preferred</div>
                  <div
                    className="cursor-pointer"
                    onClick={() =>
                      handlePreferredCandidate(candidateData, "remove", false)
                    }
                  >
                    <Cross />
                  </div>
                </div>
              ) : candidateData.is_preferred_candidate ? (
                <div className="border bg-teal-pale border-teal-dark py-4px px-8px radius-8px cursor-pointer">
                  <div className="fs-13 fw-400 text-teal-dark">Preferred</div>
                </div>
              ) : (
                <div
                  className="border border-off-white py-4px px-8px radius-8px cursor-pointer"
                  onClick={() =>
                    handlePreferredCandidate(candidateData, "mark", true)
                  }
                >
                  <div className="fs-13 fw-400 text-off-white">Preferred</div>
                </div>
              )}
            </div>
            {"blocked_by_first_name" in candidateData &&
            location.pathname.includes("blocked") ? (
              <>
                <div className="min-w-120px">
                  <div className="fs-13 fw-400 text-black">
                    {candidateData?.blocked_by_first_name}{" "}
                    {candidateData?.blocked_by_last_name}
                  </div>
                </div>
                <div className="min-w-120px">
                  <div className="fs-13 fw-400 text-black">
                    {getFormatedDate(candidateData?.blocked_date)}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="min-w-120px">
                  <div className="fs-13 fw-400 text-black">
                    {candidateData.any_centre ?? 0} Past Jobs
                  </div>
                </div>
                <div className="min-w-120px">
                  <div className="fs-13 fw-400 text-black">
                    {candidateData.your_centre ?? 0} Past Job
                  </div>
                </div>
              </>
            )}
            <div className="min-w-310px d-flex gap-20 align-items-center">
              {location.pathname.includes("blocked") ? (
                <>
                  <CustomButton
                    variant={"primary"}
                    type="button"
                    className="btn-theme bg-teal text-black"
                    onClick={() =>
                      handleViewBlockedReason?.(candidateData.uuid)
                    }
                  >
                    View Reason
                  </CustomButton>
                  <CustomButton
                    variant={"primary"}
                    type="button"
                    className="btn-theme bg-off-white text-black"
                    onClick={() => handleBlock(candidateData.id)}
                  >
                    <Repeat />
                    Unblock
                  </CustomButton>
                  <div className="position-relative">
                    <CustomButton
                      variant={"primary"}
                      className="btn-theme bg-white p-0 h-auto"
                      onClick={() => {
                        setOpenBlockedButton(!openBlockedButton);
                        setViewSelectedCandidateProfile(candidateData?.id);
                      }}
                    >
                      <VerticalDots
                        color={
                          openBlockedButton &&
                          viewSelectedCandidateProfile === candidateData?.id
                            ? "#000000"
                            : "#aaaaaa"
                        }
                      />
                    </CustomButton>
                    {openBlockedButton &&
                      viewSelectedCandidateProfile === candidateData?.id && (
                        <div
                          className="position-absolute end-0 d-flex flex-column dropdown-menu"
                          ref={openBlockedRef} // Attach ref to handle click outside detection
                        >
                          <div
                            className="dropdown-item cursor-pointer"
                            onClick={() =>
                              handleProfileView(candidateData.uuid)
                            }
                          >
                            <Eye />
                            View Profile
                          </div>
                        </div>
                      )}
                  </div>
                </>
              ) : (
                <>
                  <CustomButton
                    variant={"primary"}
                    type="button"
                    className="btn-theme bg-teal text-black"
                    onClick={() => handleProfile(candidateData.uuid)}
                  >
                    View Profile
                  </CustomButton>
                  <CustomButton
                    variant={"primary"}
                    type="button"
                    className="btn-theme bg-off-white text-black"
                    onClick={() => handleBlock(candidateData.id)}
                  >
                    <Block />
                    Block
                  </CustomButton>
                  <div className="min-w-24px position-relative d-block">
                    <CustomButton
                      variant={"primary"}
                      className="btn-theme bg-white p-0 h-auto"
                      onClick={() => {
                        setOpenSideDropdownList(!openSideDropdownList);
                        setCandidateId(candidateData.id);
                      }}
                    >
                      <VerticalDots />
                    </CustomButton>
                    {openSideDropdownList &&
                      candidateId === candidateData.id && (
                        <div
                          className="position-absolute end-0 d-flex flex-column dropdown-menu"
                          ref={openSideDropdownRef}
                        >
                          <div
                            className="dropdown-item cursor-pointer text-nowrap"
                            onClick={() =>
                              handlePreferredCandidate(
                                candidateData,
                                candidateData.is_preferred_candidate
                                  ? "remove"
                                  : "mark",
                                true
                              )
                            }
                          >
                            <Star />
                            {candidateData.is_preferred_candidate
                              ? "Remove Preferred Status"
                              : "Mark Candidate as Preferred"}
                          </div>
                        </div>
                      )}
                  </div>
                </>
              )}
            </div>
          </div>
        ))
      ) : (
        <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
          <div className="fs-16 fw-400 text-grey-medium">
            No{" "}
            {type === "preferred"
              ? "Preferred"
              : type === "blocked"
              ? "Blocked"
              : ""}{" "}
            Candidates found
          </div>
        </div>
      )}
      {candidateDatas && candidateDatas?.total > DEFAULT_ITEMS_PER_PAGE && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          paginationLinks={paginationLinks}
          onPageChange={onPageChange}
          setPerPageList={setPerPageList}
          setCurrentPage={setCurrentPage}
        />
      )}
    </>
  );
};

export default CandidateItem;
